import { Currency } from "@redotech/money/currencies";
import {
  CoverageFeeProductMatchType,
  isCoverageFeeProduct,
} from "@redotech/redo-return-coverage-common/is-coverage-fee-product";
import {
  DeliveryType,
  ExtensionShopifyClient,
  ShopifyCart,
  ShopifyCartAttributes,
  ShopifyCartItem,
  ShopifyClientType,
  ShopifyProduct,
  ShopifyProductJS,
  ShopifyShop,
} from "@redotech/shopify-client/ajax";
import { log } from "../monitoring/log";
import { isRedoItem } from "../utils";

export interface MivaBasketItem {
  /** Miva emits numeric-looking ids and skus as JSON numbers, not strings. */
  line_id: string | number;
  product_id: number;
  code: string;
  name: string;
  sku: string | number;
  price: number;
  quantity: number;
  subtotal: number;
}

export interface MivaBasketCharge {
  type: string;
  descrip: string;
  amount: number;
}

export interface MivaBasketResponse {
  success: number;
  subtotal?: number;
  items?: MivaBasketItem[];
  charges?: MivaBasketCharge[];
  error_code?: string;
  error_message?: string;
}

export interface MivaCategory {
  code: string;
  name: string;
}

export interface MivaProductCategoriesResponse {
  success: number;
  categories?: MivaCategory[];
  error_code?: string;
  error_message?: string;
}

export interface MivaCoverageFeeState {
  success: number;
  fee_applied?: number;
  fee_amount?: number;
  basket_subtotal?: number;
  error_code?: string;
  error_message?: string;
}

const DEFAULT_MIVA_ROOT = "mm5";

/** Matches the vendor `isCoverageFeeProduct` recognizes on every platform. */
const COVERAGE_VENDOR = "re:do";
/** Returned whenever the session has no basket yet, i.e. an empty cart. */
const NO_BASKET_ERROR_CODE = "redo-no-basket";
const BASKET_SETTLE_ATTEMPTS = 6;
const BASKET_SETTLE_INTERVAL_MS = 100;

/**
 * Miva has neither variants nor handles, so the product id stands in for both;
 * the CartProduct row behind coverageProductIds carries that same numeric id.
 */
function mivaItemToCartItem(
  item: MivaBasketItem,
  coverageProductIds: string[] | undefined,
): ShopifyCartItem {
  const cartItem: ShopifyCartItem = {
    id: item.product_id,
    line_id: String(item.line_id),
    // String() is load-bearing: Miva encodes numeric-looking ids and skus as
    // JSON numbers, which fail the manager's !== and any caller that lowercases.
    variant_id: String(item.product_id),
    product_id: item.product_id,
    handle: String(item.product_id),
    title: item.name,
    quantity: item.quantity,
    vendor: null,
    requires_shipping: true,
    line_price: Math.round(item.price * item.quantity * 100),
    properties: null,
    sku: String(item.sku ?? ""),
  };

  /** A second signal for consumers that have no coverage ids to match against. */
  return isRedoItem(cartItem, coverageProductIds)
    ? { ...cartItem, vendor: COVERAGE_VENDOR }
    : cartItem;
}

function isCoverageItem(
  item: MivaBasketItem,
  coverageProductIds: string[] | undefined,
): boolean {
  return isRedoItem(
    mivaItemToCartItem(item, coverageProductIds),
    coverageProductIds,
  );
}

/** Miva prices are major units; the rest of the extension works in cents. */
export function mivaBasketToCart(
  basket: MivaBasketResponse,
  coverageProductIds?: string[],
): ShopifyCart {
  const items: ShopifyCartItem[] = (basket.items ?? []).map((item) =>
    mivaItemToCartItem(item, coverageProductIds),
  );

  return {
    attributes: {},
    items,
    currency: Currency.USD,
    total_price: items.reduce((sum, item) => sum + (item.line_price ?? 0), 0),
    delivery_type: DeliveryType.SHIPPING,
  };
}

/**
 * Miva storefront client. Basket reads and coverage writes both go through the
 * Redo Miva module, because Miva has no runtime API for reading a basket.
 */
export class MivaStorefrontClient implements ExtensionShopifyClient {
  type = ShopifyClientType.MIVA;

  private readonly root: string;
  private readonly storeCode: string;
  private readonly coverageProductIds: string[] | undefined;

  constructor({
    root,
    storeCode,
    coverageProductIds,
  }: {
    root?: string;
    storeCode: string;
    coverageProductIds?: string[];
  }) {
    this.root = (root || DEFAULT_MIVA_ROOT).replace(/^\/|\/$/g, "");
    this.storeCode = storeCode;
    this.coverageProductIds = coverageProductIds;
  }

  private moduleUrl(moduleFunction: string, extra?: Record<string, string>) {
    const params = new URLSearchParams({
      Store_Code: this.storeCode,
      Function: "Module",
      Module_Code: "redo_coverage_fee",
      Module_Function: moduleFunction,
      Session_Type: "runtime",
      ...extra,
    });
    return `/${this.root}/json.mvc?${params}`;
  }

  /** json.mvc may not set a JSON content type, so the body is parsed as text. */
  private async callModule<T>(
    moduleFunction: string,
    extra?: Record<string, string>,
    signal?: AbortSignal,
  ): Promise<T> {
    const response = await fetch(this.moduleUrl(moduleFunction, extra), {
      credentials: "include",
      signal,
    });
    const body = await response.text();
    if (!response.ok) {
      throw new Error(
        `Miva ${moduleFunction} returned HTTP ${response.status}: ${body.slice(0, 200)}`,
      );
    }
    try {
      return JSON.parse(body) as T;
    } catch {
      // A WAF page or a login redirect answers 200 with HTML; without the
      // function name and body the bare SyntaxError says nothing useful.
      throw new Error(
        `Miva ${moduleFunction} returned a non-JSON body: ${body.slice(0, 200)}`,
      );
    }
  }

  private static failure(
    moduleFunction: string,
    state: { error_code?: string; error_message?: string },
  ): Error {
    return new Error(
      `Miva ${moduleFunction} failed: ${state.error_code ?? "unknown"} ${state.error_message ?? ""}`.trim(),
    );
  }

  async cartGet(signal?: AbortSignal): Promise<ShopifyCart> {
    const basket = await this.callModule<MivaBasketResponse>(
      "getBasket",
      undefined,
      signal,
    );

    if (!basket.success) {
      // Miva creates the basket lazily, so a shopper who has not added
      // anything yet gets redo-no-basket -- that is an empty cart, not a
      // failure, and throwing here would leave `cart` undefined and the
      // widget unrendered. Every other code is a real read failure, which
      // must surface: the caller would otherwise report it as an empty cart.
      if (basket.error_code === NO_BASKET_ERROR_CODE) {
        return mivaBasketToCart({ success: 1, items: [] });
      }
      throw MivaStorefrontClient.failure("getBasket", basket);
    }

    return mivaBasketToCart(basket, this.coverageProductIds);
  }

  /**
   * The module writes the basket, but a caller that reloads immediately can
   * outrun that write and read a stale basket on the next page. Poll until the
   * basket reflects what we asked for so callers can refresh safely.
   */
  private async waitForBasket(
    matches: (basket: MivaBasketResponse) => boolean,
    signal?: AbortSignal,
  ): Promise<void> {
    for (let attempt = 0; attempt < BASKET_SETTLE_ATTEMPTS; attempt++) {
      if (signal?.aborted) {
        return;
      }
      try {
        const basket = await this.callModule<MivaBasketResponse>(
          "getBasket",
          undefined,
          signal,
        );
        if (basket.success && matches(basket)) {
          return;
        }
      } catch {
        // A failed read is indistinguishable from an unsettled one here; retry.
      }
      if (attempt === BASKET_SETTLE_ATTEMPTS - 1) {
        break;
      }
      await new Promise((resolve) =>
        setTimeout(resolve, BASKET_SETTLE_INTERVAL_MS),
      );
    }
    log("Miva basket did not settle; continuing anyway.");
  }

  private hasCoverage(basket: MivaBasketResponse): boolean {
    return !!basket.items?.some((item) =>
      isCoverageItem(item, this.coverageProductIds),
    );
  }

  /** Applies the coverage charge at the price Redo calculated for this cart. */
  async attachCoverageFee(
    feeAmount: number,
    signal?: AbortSignal,
  ): Promise<MivaCoverageFeeState> {
    const state = await this.callModule<MivaCoverageFeeState>(
      "addCoverageFee",
      { Amount: feeAmount.toFixed(2) },
      signal,
    );
    if (!state.success) {
      throw MivaStorefrontClient.failure("addCoverageFee", state);
    }
    const cents = Math.round(feeAmount * 100);
    await this.waitForBasket(
      (basket) =>
        !!basket.items?.some(
          (item) =>
            isCoverageItem(item, this.coverageProductIds) &&
            Math.round(item.price * 100) === cents,
        ),
      signal,
    );
    return state;
  }

  async removeCoverageFee(signal?: AbortSignal): Promise<MivaCoverageFeeState> {
    const state = await this.callModule<MivaCoverageFeeState>(
      "removeCoverageFee",
      undefined,
      signal,
    );
    if (!state.success) {
      throw MivaStorefrontClient.failure("removeCoverageFee", state);
    }
    await this.waitForBasket((basket) => !this.hasCoverage(basket), signal);
    return state;
  }

  /** Only ever adds coverage: the module inserts the coverage product alone. */
  async cartAdd(
    data: { items: Partial<ShopifyCartItem>[] },
    params?: URLSearchParams,
    preventReload?: boolean,
    coveragePrice?: number,
  ): Promise<unknown> {
    // An item being added is built from /fees, so it carries an id but no
    // vendor or sku yet -- the id is the only signal worth checking.
    const item = data.items[0];
    if (item && this.coverageProductIds?.length) {
      const isCoverage = isCoverageFeeProduct({
        product: { product_id: item.product_id },
        redoCoverageProductIds: new Set(this.coverageProductIds),
        matchType: CoverageFeeProductMatchType.STRICT_PRODUCT_ID_CHECK,
      });
      if (!isCoverage) {
        throw new Error(
          `Miva cartAdd only adds Redo coverage, but was given ${item.title ?? item.variant_id}`,
        );
      }
    }
    if (!coveragePrice) {
      log("cartAdd() called without a coverage price; nothing to attach.");
      return;
    }
    return await this.attachCoverageFee(coveragePrice);
  }

  /** Only ever removes coverage: the module deletes by coverage product id. */
  async removeItemFromCart(
    item?: ShopifyCartItem,
    numToRemove?: number,
    signal?: AbortSignal,
  ): Promise<undefined> {
    if (item && !isRedoItem(item, this.coverageProductIds)) {
      throw new Error(
        `Miva removeItemFromCart only removes Redo coverage, but was given ${item.title ?? item.variant_id}`,
      );
    }
    await this.removeCoverageFee(signal);
    return undefined;
  }

  async cartClear(): Promise<void> {
    return;
  }

  async updateCartAttribute(
    attributes: ShopifyCartAttributes,
    signal?: AbortSignal,
  ): Promise<ShopifyCart | void> {
    log("updateCartAttribute() was called, but is unsupported for Miva.");
  }

  async deleteCartAttribute(): Promise<void> {
    log("deleteCartAttribute() was called, but is unsupported for Miva.");
  }

  async updateCartItemSellingPlan(): Promise<undefined> {
    log("updateCartItemSellingPlan() was called, but is unsupported for Miva.");
  }

  /**
   * Miva's runtime API only exposes a product's canonical category, an SEO
   * field that misses the rest, so this goes through the module instead.
   */
  async getCollections(
    handle?: string,
    signal?: AbortSignal,
  ): Promise<{ title: string; handle: string }[] | undefined> {
    if (!handle) {
      console.warn("Miva product id is missing.");
      return undefined;
    }
    try {
      const response = await this.callModule<MivaProductCategoriesResponse>(
        "getProductCategories",
        { Product_ID: handle },
        signal,
      );
      if (!response.success) {
        console.error(
          `Miva getProductCategories failed for ${handle}: ${response.error_code ?? "unknown"} ${response.error_message ?? ""}`.trim(),
        );
        return undefined;
      }
      // Exclusions match on title; handle keeps the stable Miva category code.
      return (response.categories ?? []).map((category) => ({
        title: String(category.name ?? ""),
        handle: String(category.code ?? ""),
      }));
    } catch (e) {
      console.error("Error getting Miva categories", { handle, error: e });
      return undefined;
    }
  }

  async productGet(): Promise<ShopifyProduct> {
    throw new Error("Unsupported");
  }

  async productGetJS(): Promise<ShopifyProductJS> {
    throw new Error("Unsupported");
  }

  async getShopMetafield(): Promise<ShopifyShop | undefined> {
    throw new Error("Unsupported");
  }
}
